import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PreliminaryReservationService from '../services/PreliminaryReservationService';
import { clearSessionId } from '../actions/session';

const RESERVATION_DURATION = 15 * 60; // 15 minutes in seconds
const WARNING_TIME = 2 * 60; // Show warning 2 minutes before expiry

export default function useReservationTimeout(trainId) {
    const [showWarning, setShowWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(RESERVATION_DURATION);
    const [reservationStartTime, setReservationStartTime] = useState(Date.now());
    const sessionId = useSelector(state => state.session);
    const history = useHistory();
    const dispatch = useDispatch();

    useEffect(() => {
        if (sessionId) {
            setReservationStartTime(Date.now());
            setTimeRemaining(RESERVATION_DURATION);
            setShowWarning(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (!sessionId) return;

        const interval = setInterval(() => {
            const elapsed = Math.floor((Date.now() - reservationStartTime) / 1000);
            const remaining = RESERVATION_DURATION - elapsed;

            setTimeRemaining(remaining);

            if (remaining <= WARNING_TIME && remaining > 0 && !showWarning) {
                setShowWarning(true);
            }

            if (remaining <= 0) {
                handleTimeout();
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [sessionId, reservationStartTime, showWarning]);

    const handleExtend = useCallback(() => {
        if (!sessionId || !trainId) return;

        PreliminaryReservationService.extend(sessionId, trainId)
            .then(() => {
                // Reset the timer
                setReservationStartTime(Date.now());
                setTimeRemaining(RESERVATION_DURATION);
                setShowWarning(false);
            })
            .catch(e => {
                console.error("Failed to extend preliminary reservation:", e);
                handleTimeout();
            });
    }, [sessionId, trainId]);

    const handleTimeout = useCallback(() => {
        if (sessionId && trainId) {
            PreliminaryReservationService.release(sessionId, trainId)
                .catch(e => console.error("Failed to release preliminary reservations:", e));
        }

        dispatch(clearSessionId());
        setShowWarning(false);

        // Full page reload to clear all Redux state
        sessionStorage.setItem('timeoutMessage', 'Din bokningssession har gått ut på grund av inaktivitet. Vänligen börja om din bokning.');
        window.location.href = '/';
    }, [sessionId, trainId, dispatch]);

    const dismissWarning = useCallback(() => {
        handleTimeout();
    }, [handleTimeout]);

    return {
        showWarning,
        timeRemaining,
        handleExtend,
        handleTimeout: dismissWarning,
    };
}
